import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assessment,
  Chat,
  AccessTime,
  CheckCircle,
  Star,
  Person,
  Label,
  Visibility,
  FilterList
} from '@mui/icons-material';
import { advancedReportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface ConversationAuditsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent: number | 'all';
}

interface ConversationAudit {
  conversation_id: number;
  contact_name: string;
  agent_name: string | null;
  channel_type: string;
  created_at: string;
  resolved_at: string | null;
  status: string;
  priority: string;
  first_response_time: number | null;
  resolution_time: number | null;
  message_count: number;
  agent_message_count: number;
  customer_message_count: number;
  sentiment_score: number;
  csat_rating: number | null;
  labels: string[];
}

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved': return 'success';
      case 'open': return 'primary';
      case 'pending': return 'warning';
      case 'snoozed': return 'info';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={status.toUpperCase()}
      color={getStatusColor(status) as any}
      size="small"
    />
  );
};

const PriorityChip: React.FC<{ priority: string }> = ({ priority }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={priority.toUpperCase()}
      color={getPriorityColor(priority) as any}
      size="small"
      variant="outlined"
    />
  );
};

const SentimentIndicator: React.FC<{ score: number }> = ({ score }) => {
  const getSentimentColor = (score: number) => {
    if (score > 0.1) return 'success.main';
    if (score < -0.1) return 'error.main';
    return 'warning.main';
  };

  const getSentimentLabel = (score: number) => {
    if (score > 0.1) return 'Positive';
    if (score < -0.1) return 'Negative';
    return 'Neutral';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: getSentimentColor(score),
          mr: 1
        }}
      />
      <Typography variant="caption">
        {getSentimentLabel(score)}
      </Typography>
    </Box>
  );
};

export const ConversationAudits: React.FC<ConversationAuditsProps> = ({ 
  dateRange, 
  selectedAgent 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audits, setAudits] = useState<ConversationAudit[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  useEffect(() => {
    fetchAudits();
  }, [dateRange, selectedAgent, page, statusFilter, priorityFilter]);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0],
        page,
        limit: 20
      };

      if (selectedAgent !== 'all') {
        params.agent_id = selectedAgent;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (priorityFilter) {
        params.priority = priorityFilter;
      }

      const data = await advancedReportsAPI.getConversationAudits(params);
      setAudits(data);
      
      // Calculate total pages (assuming 20 items per page)
      setTotalPages(Math.max(1, Math.ceil(data.length / 20)));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversation audits');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number | null) => {
    if (!minutes) return 'N/A';
    // Round to 1 decimal place for display
    const roundedMinutes = Math.round(minutes * 10) / 10;
    if (roundedMinutes < 60) return `${roundedMinutes}m`;
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = Math.round(roundedMinutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Prepare data for export
  const exportData = audits || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Assessment sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Conversation Audits
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="conversation_audits"
          metadata={{ dateRange, filters: { agent: selectedAgent, status: statusFilter, priority: priorityFilter } }}
        />
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="snoozed">Snoozed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStatusFilter('');
                  setPriorityFilter('');
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {audits.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {audits.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Conversations
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {audits.filter(a => a.status === 'resolved').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Resolved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  {formatTime(Math.round((audits.reduce((sum, a) => sum + (a.first_response_time || 0), 0) / audits.length) * 10) / 10)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg First Response
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main">
                  {(audits.reduce((sum, a) => sum + (a.csat_rating || 0), 0) / audits.filter(a => a.csat_rating).length || 0).toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg CSAT Score
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Audits Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Conversation Details
          </Typography>
          
          {audits.length === 0 ? (
            <Alert severity="info">
              No conversation audits found for the selected criteria.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Conversation</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Messages</TableCell>
                      <TableCell>Response Time</TableCell>
                      <TableCell>Sentiment</TableCell>
                      <TableCell>CSAT</TableCell>
                      <TableCell>Labels</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audits.map((audit) => (
                      <TableRow key={audit.conversation_id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              #{audit.conversation_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(audit.created_at)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {audit.contact_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {audit.agent_name ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Person sx={{ mr: 0.5, fontSize: 16 }} />
                              <Typography variant="body2">
                                {audit.agent_name}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Unassigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={audit.channel_type.toUpperCase()}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={audit.status} />
                        </TableCell>
                        <TableCell>
                          <PriorityChip priority={audit.priority} />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {audit.message_count} total
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {audit.customer_message_count} customer, {audit.agent_message_count} agent
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTime(audit.first_response_time)}
                          </Typography>
                          {audit.resolution_time && (
                            <Typography variant="caption" color="text.secondary">
                              Resolved in {formatTime(audit.resolution_time)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <SentimentIndicator score={audit.sentiment_score} />
                        </TableCell>
                        <TableCell>
                          {audit.csat_rating ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Star sx={{ color: 'warning.main', mr: 0.5, fontSize: 16 }} />
                              <Typography variant="body2">
                                {audit.csat_rating}/5
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No rating
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {audit.labels.slice(0, 2).map((label, index) => (
                              <Chip
                                key={index}
                                label={label}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                            {audit.labels.length > 2 && (
                              <Chip
                                label={`+${audit.labels.length - 2}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};